import {FC, useState, useEffect, useCallback, useRef} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Image, Alert} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {CountryOverviewTable} from './components/CountryOverviewTable'
import {enqueueCountryOverview, getCountryOverviewJobStatus} from './core/_requests'
import {CountryOverviewRow} from './core/_models'
import {getOverviewCache, setOverviewCache} from './core/_overviewCache'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useSocket} from '../../../contexts/SocketContext'

const POLL_INTERVAL_MS = 2000

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear()
const DEFAULT_YEAR_TO = CURRENT_YEAR - 1
const DEFAULT_YEAR_FROM = CURRENT_YEAR - 5

function buildYearOptions() {
  const opts: number[] = []
  for (let y = 2015; y <= CURRENT_YEAR - 1; y++) opts.push(y)
  return opts
}

function buildYearRange(yFrom: number, yTo: number) {
  const range: number[] = []
  for (let y = yFrom; y <= yTo; y++) range.push(y)
  return range
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────

const TradeDataManagementV2Page: FC<{subject: string}> = ({subject}) => {
  const intl = useIntl()
  const axiosPrivate = useAxiosPrivate()
  const {socket, connected} = useSocket()

  // Restores from the in-memory cache if we've already loaded once this SPA
  // session (e.g. coming back from the country/HS detail page via browser
  // Back) — lets us skip the loading spinner and re-queue entirely.
  const cached = getOverviewCache()

  // Default: 5 years back from last completed year (e.g. 2021–2025 in 2026)
  const [yearFrom, setYearFrom] = useState(cached?.appliedYearFrom ?? DEFAULT_YEAR_FROM)
  const [yearTo, setYearTo] = useState(cached?.appliedYearTo ?? DEFAULT_YEAR_TO)

  // Applied values (only updated when user clicks Apply)
  const [appliedYearFrom, setAppliedYearFrom] = useState(cached?.appliedYearFrom ?? DEFAULT_YEAR_FROM)
  const [appliedYearTo, setAppliedYearTo] = useState(cached?.appliedYearTo ?? DEFAULT_YEAR_TO)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [countries, setCountries] = useState<CountryOverviewRow[]>(cached?.countries ?? [])
  const [years, setYears] = useState<number[]>(cached?.years ?? [])

  // Tracks whatever cleanup the *previous* in-flight job needs (leave socket
  // room + remove listener, or clear the polling interval) so a new Apply
  // click — or unmount — can't leak a listener/interval watching a stale job.
  const cleanupJobWatchRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      cleanupJobWatchRef.current?.()
    }
  }, [])

  const applyResult = useCallback((data: any, yFrom: number, yTo: number) => {
    const nextCountries = data?.data?.countries ?? []
    const nextYears = data?.years ?? []
    setCountries(nextCountries)
    setYears(nextYears)
    setOverviewCache({
      appliedYearFrom: yFrom,
      appliedYearTo: yTo,
      countries: nextCountries,
      years: nextYears,
    })
  }, [])

  // Merges a progressive (non-final) snapshot sent by the worker as it fills
  // in one HS+year column at a time — swaps skeleton placeholders for real
  // badges column by column, well before the whole scan finishes. Not
  // written to the overview cache: only the terminal result (applyResult)
  // counts as a "loaded" state worth restoring on browser Back.
  const applyPartial = useCallback((partial: any) => {
    if (!partial) return
    if (partial.countries) setCountries(partial.countries)
    if (partial.years) setYears(partial.years)
  }, [])

  // Enqueue a background job (BullMQ, its own dedicated queue — see
  // tu-be/services/tradeDataV2Queue.service.js) then either listen on the
  // tdv2job-{id} socket room for live progress, or fall back to polling the
  // job-status endpoint every few seconds. Scanning every HS version x year
  // against Comtrade is dozens of throttled external calls, too slow to do
  // inside a single blocking request without risking a gateway timeout.
  const fetchData = useCallback(
    (yFrom: number, yTo: number, force: boolean = false) => {
      cleanupJobWatchRef.current?.()
      cleanupJobWatchRef.current = null

      setIsLoading(true)
      setError(null)
      setCountries([])
      // Set the year columns immediately (before the job even starts) so the
      // table can render its full grid of skeleton cells right away, rather
      // than waiting for the first partial payload to know how many columns
      // to draw.
      setYears(buildYearRange(yFrom, yTo))

      // Guards against the socket path and the safety-net poll both firing
      // handleTerminal for the same job (e.g. both were mid-flight when the
      // job finished) — only the first one should actually apply anything.
      let settled = false

      const handleTerminal = (
        state: 'completed' | 'failed',
        returnValue: any,
        failedReason?: string
      ) => {
        if (settled) return
        settled = true

        if (state === 'completed') {
          applyResult(returnValue, yFrom, yTo)
        } else {
          setError(failedReason || 'The background scan failed.')
        }
        setIsLoading(false)
        cleanupJobWatchRef.current?.()
        cleanupJobWatchRef.current = null
      }

      enqueueCountryOverview(axiosPrivate, {year_from: yFrom, year_to: yTo, force})
        .then((res: any) => {
          const jobId = res?.data?.jobId
          if (!jobId) throw new Error('Server did not return a job id')

          if (socket && connected) {
            // "tdv2job-" prefix, not "job-" — Trade Data V2 runs on its own
            // dedicated BullMQ queue (tradeDataV2Queue), separate from the
            // shared importQueue used elsewhere in the app. Job IDs are only
            // unique per-queue, so a shared "job-{id}" room name would risk
            // colliding with an unrelated job of the same id on importQueue.
            const roomName = `tdv2job-${jobId}`
            socket.emit('join-room', roomName)

            const handleProgress = (data: any) => {
              if (data.partial) applyPartial(data.partial)

              if (data.status === 'completed') {
                handleTerminal('completed', data.returnValue)
              } else if (data.status === 'failed') {
                handleTerminal('failed', null, data.failedReason)
              }
            }

            socket.on('progress', handleProgress)

            // Safety net: on an all-cached (or otherwise very fast) run, the
            // job can finish before the server has even processed our
            // "join-room" message — every progress/completed event it
            // broadcast in that window never reaches this socket, and since
            // the job is already done, no further events will ever come
            // either. That left the page stuck on the skeleton forever. A
            // slower backup poll (independent of the socket) catches that
            // race without giving up the low-latency socket path for the
            // normal case.
            const safetyPollId = setInterval(async () => {
              try {
                const statusRes = await getCountryOverviewJobStatus(axiosPrivate, jobId)
                const {state, progress, failedReason, returnValue} = statusRes.data

                if (progress?.partial) applyPartial(progress.partial)

                if (state === 'completed') {
                  handleTerminal('completed', returnValue)
                } else if (state === 'failed') {
                  handleTerminal('failed', null, failedReason)
                }
              } catch (e: any) {
                console.error('Error polling country-overview job status (safety net):', e)
              }
            }, POLL_INTERVAL_MS)

            cleanupJobWatchRef.current = () => {
              socket.emit('leave-room', roomName)
              socket.off('progress', handleProgress)
              clearInterval(safetyPollId)
            }
          } else {
            // Fallback to polling when the socket isn't connected
            const intervalId = setInterval(async () => {
              try {
                const statusRes = await getCountryOverviewJobStatus(axiosPrivate, jobId)
                const {state, progress, failedReason, returnValue} = statusRes.data

                if (progress?.partial) applyPartial(progress.partial)

                if (state === 'completed') {
                  clearInterval(intervalId)
                  handleTerminal('completed', returnValue)
                } else if (state === 'failed') {
                  clearInterval(intervalId)
                  handleTerminal('failed', null, failedReason)
                }
              } catch (e: any) {
                console.error('Error polling country-overview job status:', e)
              }
            }, POLL_INTERVAL_MS)

            cleanupJobWatchRef.current = () => clearInterval(intervalId)
          }
        })
        .catch((e: any) => {
          const msg =
            e?.response?.data?.message || e?.message || 'Failed to queue the scan'
          setError(msg)
          setIsLoading(false)
        })
    },
    [axiosPrivate, socket, connected, applyResult, applyPartial]
  )

  // Auto-load on mount with defaults — but only if we don't already have a
  // cached result (e.g. returning via browser Back from the detail page).
  // Re-queuing a full Comtrade scan just to redisplay data that's already
  // on screen a second ago is exactly the "loading again" pain this avoids.
  useEffect(() => {
    if (!cached) {
      fetchData(appliedYearFrom, appliedYearTo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleApply = () => {
    if (yearFrom > yearTo) {
      setError('"Year From" cannot be later than "Year To".')
      return
    }
    setAppliedYearFrom(yearFrom)
    setAppliedYearTo(yearTo)
    fetchData(yearFrom, yearTo, false)
  }

  // Forces a live Comtrade re-check for the currently applied year range,
  // bypassing the backend's once-a-day cache — for when the user knows
  // Comtrade has published an update since the last scan today.
  const handleForceRefresh = () => {
    fetchData(appliedYearFrom, appliedYearTo, true)
  }

  const yearOptions = buildYearOptions()

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Home', path: '/', isSeparator: false, isActive: false},
          {title: '', path: '', isSeparator: true, isActive: false},
        ]}
      >
        {intl.formatMessage({id: subject})}
      </PageTitle>

      {/* Hero card */}
      <Row className='mb-5'>
        <Col>
          <Card className='card-custom card-border'>
            <Card.Body className='d-flex justify-content-around align-items-center'>
              <Image src='/media/tina/img-result-market.jpg' thumbnail width={110} />
              <div className='ms-5 d-flex flex-column justify-content-center'>
                <h3 className='text-primary'>Trade Flow Data — Country Overview</h3>
                <div className='text-gray-700'>
                  View Comtrade data availability per country, year, and HS version (H4 / H5 / H6).
                  Each cell shows whether data is available on Comtrade, already synced to TINA, or
                  has a newer update waiting to be imported.
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filter card */}
      <Row className='mb-5'>
        <Col>
          <Card>
            <Card.Header className='card-header-stretch'>
              <Card.Title>
                <span className='fa fa-sliders px-2' />
                <h3 className='text-gray-700'>Year Range</h3>
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Row className='align-items-end g-3'>
                <Col lg={3} md={4} sm={6}>
                  <Form.Group>
                    <Form.Label className='fw-semibold'>Year From</Form.Label>
                    <Form.Select
                      value={yearFrom}
                      onChange={(e) => setYearFrom(Number(e.target.value))}
                    >
                      {yearOptions.map((y) => (
                        // Can't start later than "Year To" — years after it
                        // are disabled instead of just erroring on Apply.
                        <option key={y} value={y} disabled={y > yearTo}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg={3} md={4} sm={6}>
                  <Form.Group>
                    <Form.Label className='fw-semibold'>Year To</Form.Label>
                    <Form.Select
                      value={yearTo}
                      onChange={(e) => setYearTo(Number(e.target.value))}
                    >
                      {yearOptions.map((y) => (
                        // Can't end earlier than "Year From" — e.g. if Year
                        // From is 2020, 2019 and earlier aren't selectable.
                        <option key={y} value={y} disabled={y < yearFrom}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg={2} md={4} sm={6}>
                  <Button
                    variant='primary'
                    className='w-100'
                    onClick={handleApply}
                    disabled={isLoading}
                  >
                    Apply
                  </Button>
                </Col>
                <Col lg={2} md={4} sm={6}>
                  <Button
                    variant='outline-secondary'
                    className='w-100'
                    onClick={handleForceRefresh}
                    disabled={isLoading}
                    title='Ignores today&apos;s cached Comtrade result and re-checks live — use this if you know Comtrade published an update today.'
                  >
                    <i className='fa-solid fa-rotate me-2' />
                    Check update
                  </Button>
                </Col>
              </Row>

              {error && (
                <Alert variant='danger' className='mt-4' dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Results card */}
      {(isLoading || countries.length > 0) && (
        <Row>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title>
                  <span className='fa fa-table px-2' />
                  <h3 className='text-gray-700'>
                    Comtrade Availability
                    {!isLoading && years.length > 0 && (
                      <span className='text-muted fw-normal fs-6 ms-2'>
                        ({years[0]}–{years[years.length - 1]})
                      </span>
                    )}
                  </h3>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <CountryOverviewTable rows={countries} years={years} isLoading={isLoading} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  )
}

export default TradeDataManagementV2Page
